import { z } from 'zod';
import { nonEmptyPatch } from '../_patch.js';
import { StorageObjectKeySchema } from '../storage.js';

export const RequestRouteSchema = z
  .object({
    from: z.string().min(1),
    to: z.string().min(1),
  })
  .strict();
export type RequestRoute = z.infer<typeof RequestRouteSchema>;

/**
 * @aiPurpose dimensions/places workflow (#590). Bounds and constants
 *   for `RequestItemPackage` rows. Centralised so the web inputs,
 *   backend recompute, and XLSX export all reference one source.
 * @aiInvariant integer centimeters for width/depth/height, capped at
 *   `REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM` so a single tampered
 *   payload cannot overflow downstream `RequestItem.{volume,weight}`
 *   `Decimal(13,4)` precision. Per-package m³ volume formula =
 *   `widthCm * depthCm * heightCm / 1_000_000`. Per-package count
 *   capped at the existing `RequestItem.quantity` max so a
 *   dimensions-mode item's computed quantity cannot exceed the wire
 *   bound.
 * @aiSecurity the bounds are NOT a security control on their own —
 *   they are a finiteness guarantee. Authority/visibility on item
 *   writes flows through the existing request sub-resources / draft
 *   gates (issue #590 Resolved Decision §21).
 */
export const REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM = 100000;
export const REQUEST_ITEM_PACKAGE_COUNT_MAX = 999999;
export const REQUEST_ITEM_PACKAGE_WEIGHT_KG_MAX = 999_999_999.9999;
// WHY (#1449 / #1450): per-position photo cap raised from 1 to 5.
//   Box-item photos stay at 1 by epic #1449 decision D7.
export const REQUEST_ITEM_PHOTOS_MAX = 5;
// WHY (#590): item volume in dimensions mode is the sum of per-package
//   volumes rounded to this precision before persistence so the wire
//   surface keeps reading the existing `RequestItem.volume Decimal(13,4)`
//   shape (Resolved Decision §9). Exported so backend / web preview /
//   tests round through the same constant.
export const REQUEST_ITEM_DIMENSIONS_VOLUME_PRECISION_M3 = 0.0001;

/**
 * @aiPurpose wire projection of one persisted `RequestItemPackage` row
 *   on a request item. Optional fields on the response item schema
 *   make this a rollout-tolerant addition: legacy/default callers do
 *   NOT receive the `packages` / `dimensionsMode` keys (response
 *   shape is shrunk by the backend for those callers per Resolved
 *   Decision §25), while opted-in callers parse the expanded shape
 *   here.
 * @aiInvariant `id` is a server-minted UUID; `sortOrder` is the
 *   deterministic UI/export order. Dimensions and count are integers;
 *   `weightKg` is a number in kilograms with `Decimal(13,4)` precision
 *   round-trip semantics.
 */
export const RequestItemPackageSchema = z
  .object({
    id: z.string().uuid(),
    sortOrder: z.number().int().nonnegative(),
    widthCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    depthCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    heightCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    weightKg: z.number().nonnegative().max(REQUEST_ITEM_PACKAGE_WEIGHT_KG_MAX),
    count: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_COUNT_MAX),
  })
  .strict();
export type RequestItemPackage = z.infer<typeof RequestItemPackageSchema>;

/**
 * @aiPurpose package input for the regular item create/patch path
 *   (`POST /requests/:id/items`, `PATCH /requests/:id/items/:itemId`).
 *   `id` is optional: omitted for new rows (server mints UUID),
 *   present for updates. `sortOrder` is also optional — when omitted
 *   the service assigns the input array index.
 * @aiInvariant cross-item ownership is enforced by the requests
 *   sub-resources service: a non-null `id` must already belong to the
 *   addressed item. Reuse of an id from a different request item is
 *   rejected with the existing invisible/not-found error so the
 *   caller cannot probe foreign rows.
 */
export const CreateRequestItemPackageInputSchema = z
  .object({
    id: z.string().uuid().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
    widthCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    depthCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    heightCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    weightKg: z.number().nonnegative().max(REQUEST_ITEM_PACKAGE_WEIGHT_KG_MAX),
    count: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_COUNT_MAX),
  })
  .strict();
export type CreateRequestItemPackageInput = z.infer<typeof CreateRequestItemPackageInputSchema>;

export const RequestItemSchema = z
  .object({
    id: z.string().regex(/^it-\d{4}$/, 'must match it-NNNN'),
    name: z.string().min(1),
    brand: z.string(),
    // WHY (#701): nullable on read — CONTAINER-kind requests may emit
    //   items without weight/volume because the parent transport type
    //   carries the canonical capacity. STANDARD rows must still carry
    //   non-null values; `enforceActiveStrictness` enforces that on the
    //   active half of the state machine.
    weight: z.number().nonnegative().nullable(),
    volume: z.number().nonnegative().nullable(),
    quantity: z.number().int().min(1).max(999999),
    // WHY (#1050): product unit count, distinct from `quantity`
    //   (the logistics/places mirror). Nullable on read so legacy rows
    //   (CONTAINER + dimensions-mode + manual STANDARD pre-#1050)
    //   round-trip without a 1:1 default. Positive integer within the
    //   same upper bound as `quantity` when present.
    productQuantity: z.number().int().min(1).max(999999).nullable().optional(),
    material: z.string().optional(),
    description: z.string(),
    // INVARIANT: up to REQUEST_ITEM_PHOTOS_MAX photos per item (epic #1449).
    // The 10MB byte cap is enforced by the storage capsule's presigned-URL
    // Content-Length policy, not at the wire layer. Each entry is a
    // StorageObjectKeySchema (non-empty) so a broken `''` reference
    // cannot be persisted from a request payload.
    photos: z.array(StorageObjectKeySchema).max(REQUEST_ITEM_PHOTOS_MAX),
    // WHY (#590): rolling-deploy tolerant expanded shape. Optional so
    //   stale browser bundles that never project the keys keep
    //   parsing legacy/default backend responses (which omit the keys
    //   entirely) AND so new web bundles parse responses from a
    //   not-yet-upgraded backend without rejecting. Backend emits the
    //   keys only for callers that opted in (`includeItemPackages`
    //   detail flag) or that touched the new dimensions fields in
    //   their mutation body (Resolved Decision §25).
    dimensionsMode: z.boolean().optional(),
    packages: z.array(RequestItemPackageSchema).optional(),
  })
  .strict();
export type RequestItem = z.infer<typeof RequestItemSchema>;

// WHY: draft-like rows (`DRAFT` and `RETURN`) are intentionally allowed to
// round-trip exact partial values that an active row could never carry — a
// half-typed item with no name yet, quantity still at the empty-UI
// sentinel `0`, an empty description placeholder. The relaxed shape is
// structurally identical to `RequestItemSchema` (same keys, `.strict()`,
// same REQUEST_ITEM_PHOTOS_MAX cap, server-minted `id`) but lifts
// `name.min(1)` and `quantity.min(1)`. Active item strictness is
// re-asserted at the request level via `enforceActiveStrictness` when the
// status is not draft-like. Storage object keys are still validated
// non-empty — an empty-string photo reference is broken regardless of status.
export const DraftRequestItemSchema = z
  .object({
    id: z.string().regex(/^it-\d{4}$/, 'must match it-NNNN'),
    name: z.string(),
    brand: z.string(),
    // WHY (#701): nullable for the same reason `RequestItemSchema` is —
    //   CONTAINER-kind drafts/RETURN rows may carry null on these
    //   columns; the active-strictness refinement does not gate
    //   draft-like statuses anyway.
    weight: z.number().nonnegative().nullable(),
    volume: z.number().nonnegative().nullable(),
    quantity: z.number().int().min(0).max(999999),
    // WHY (#1050): same shape as active read but nullable for draft
    //   round-trip. Draft-save may carry `null` (manager hasn't filled
    //   the SKU count yet) or a positive integer.
    productQuantity: z.number().int().min(1).max(999999).nullable().optional(),
    material: z.string().optional(),
    description: z.string(),
    photos: z.array(StorageObjectKeySchema).max(REQUEST_ITEM_PHOTOS_MAX),
    // WHY (#590): same rolling-deploy tolerance as `RequestItemSchema`.
    //   Optional on draft-like items so stale-bundle parsing stays safe.
    dimensionsMode: z.boolean().optional(),
    packages: z.array(RequestItemPackageSchema).optional(),
  })
  .strict();
export type DraftRequestItem = z.infer<typeof DraftRequestItemSchema>;

const RequestItemWriteBaseShape = RequestItemSchema.omit({
  id: true,
  dimensionsMode: true,
  packages: true,
})
  .extend({
    description: z.string().min(1),
    // WHY (#701): nullable so CONTAINER-kind requests can write items
    //   without weight/volume. The STANDARD-kind requirement (non-null)
    //   is enforced at the parent-level refine on
    //   `CreateRequestInputSchema` / `PatchRequestInputSchema`, which
    //   has access to the discriminator. Single-item endpoints
    //   (`POST /requests/:id/items`, `PATCH /requests/:id/items/:id`)
    //   don't carry the parent kind in their body, so the service-
    //   layer invariant on those paths reads the parent Request and
    //   applies the same gate.
    weight: z.number().positive().nullable(),
    volume: z.number().positive().nullable(),
    quantity: z.number().int().min(1).max(999999),
    // WHY (#590): regular item create/patch input may flip dimensions
    //   mode and supply package rows. When `dimensionsMode === true` the
    //   service ignores the manual `weight`/`volume`/`quantity` on the
    //   input and recomputes them from `packages`. When omitted /
    //   false, the service treats this as a manual-mode write — package
    //   rows that arrive in the body are persisted but inert (so the
    //   client can pre-stage rows without flipping the mode).
    dimensionsMode: z.boolean().optional(),
    packages: z.array(CreateRequestItemPackageInputSchema).max(500).optional(),
  })
  .strict();

// WHY (#590): the active-write refine — `dimensionsMode=true` must
//   carry at least one package row and every package field must be
//   strictly positive. Identical to the send-readiness gate; applied
//   on regular create/patch so an active item can't slip past the gate
//   with a half-typed package.
type DimensionsRefineShape = {
  readonly dimensionsMode?: boolean | undefined;
  readonly packages?: ReadonlyArray<CreateRequestItemPackageInput> | undefined;
};
const dimensionsActiveRefine = (it: DimensionsRefineShape, ctx: z.RefinementCtx): void => {
  if (it.dimensionsMode !== true) return;
  const pkgs = it.packages;
  if (!pkgs || pkgs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'dimensions-mode item requires at least one package row',
      path: ['packages'],
    });
    return;
  }
  for (let i = 0; i < pkgs.length; i += 1) {
    const p = pkgs[i];
    if (!p) continue;
    if (p.widthCm <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'widthCm'],
        message: 'Number must be greater than or equal to 1',
      });
    }
    if (p.depthCm <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'depthCm'],
        message: 'Number must be greater than or equal to 1',
      });
    }
    if (p.heightCm <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'heightCm'],
        message: 'Number must be greater than or equal to 1',
      });
    }
    if (p.weightKg <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 0,
        type: 'number',
        inclusive: false,
        path: ['packages', i, 'weightKg'],
        message: 'Number must be greater than 0',
      });
    }
    if (p.count <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'count'],
        message: 'Number must be greater than or equal to 1',
      });
    }
  }
};

export const CreateRequestItemInputSchema =
  RequestItemWriteBaseShape.superRefine(dimensionsActiveRefine);
export type CreateRequestItemInput = z.infer<typeof CreateRequestItemInputSchema>;

// WHY (#590): patch-aware refine. The create-side `dimensionsActiveRefine`
//   demands `packages` whenever `dimensionsMode === true`, but a PATCH
//   body may legitimately flip the mode without re-listing already-
//   persisted rows (server uses `current.packages`). So: when the body
//   supplies `packages`, every row must be strictly positive — same
//   bound as the create refine. When `packages` is absent, defer to the
//   service-layer check (`effectivePackages.length === 0` →
//   BadRequestException). Per-row positive checks here close the wire
//   contract gap the create-side refine has on the patch path so a
//   regular active-item patch cannot persist a zero-field package row.
const dimensionsPatchRefine = (it: DimensionsRefineShape, ctx: z.RefinementCtx): void => {
  const pkgs = it.packages;
  if (pkgs === undefined) return;
  if (it.dimensionsMode === true && pkgs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'dimensions-mode item requires at least one package row',
      path: ['packages'],
    });
    return;
  }
  if (it.dimensionsMode !== true) return;
  for (let i = 0; i < pkgs.length; i += 1) {
    const p = pkgs[i];
    if (!p) continue;
    if (p.widthCm <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'widthCm'],
        message: 'Number must be greater than or equal to 1',
      });
    }
    if (p.depthCm <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'depthCm'],
        message: 'Number must be greater than or equal to 1',
      });
    }
    if (p.heightCm <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'heightCm'],
        message: 'Number must be greater than or equal to 1',
      });
    }
    if (p.weightKg <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 0,
        type: 'number',
        inclusive: false,
        path: ['packages', i, 'weightKg'],
        message: 'Number must be greater than 0',
      });
    }
    if (p.count <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'number',
        inclusive: true,
        path: ['packages', i, 'count'],
        message: 'Number must be greater than or equal to 1',
      });
    }
  }
};

export const PatchRequestItemInputSchema = nonEmptyPatch(
  RequestItemWriteBaseShape.partial().superRefine(dimensionsPatchRefine),
);
export type PatchRequestItemInput = z.infer<typeof PatchRequestItemInputSchema>;
